
<template>
  <div>
    <editor-stepper
    />
    <loader v-if="loading"
      :label="$t('editor.loadingDecisionPolicy')"
    />
    <v-container
      v-else
    >
      <div class="decision-policy">
        <v-data-table
          :headers="headers"
          :items="items"
          :items-per-page.sync="itemsPerPage"
          :page.sync="page"
          hide-default-header
          hide-default-footer
          >
          <template v-slot:header="{ props }">
            <thead>
              <tr>
                <th class="fixed text-center scenario-header pa-1">
                  <input-scenario
                    :available-elements.sync="decisionPolicy.elements"
                    :elements.sync="elementsFilter"
                    :enable-element-creation="false"
                    label="decisionPolicy.filterByElements"
                    v-on="$listeners"
                  />
                </th>
                <template v-for="header in props.headers">
                  <th
                    v-bind:key="`header-${header.value}`"
                    :class="`${ header.noScenarioLinked ? 'scenario-header-alert' : 'scenario-header '}  text-center`"
                  >
                    <div class="rai-wigo clickable" @click="onOptionSelected(header.option)">
                      <option-chip-2
                        class="align-self-end ma-1 rai-wigo"
                        :option="header.option"
                        :clickable="true"
                        :show-details="debugMode"
                      />
                      <v-icon>
                        <template v-if="optionsFilter.includes(header.option)">
                          mdi-filter-check
                        </template>
                        <template v-else>
                          mdi-filter-outline
                        </template>
                      </v-icon>
                    </div>
                  </th>
                </template>
              </tr>
            </thead>
          </template>
          <template v-slot:item="{ item }">
            <tr
              :class="`${item.scenario.noOptionLinked ? 'alert' : ''}`"
            >
              <template
              v-for="(propertyValue, propertyName) in item"
              >
              <th
                v-if="propertyName === 'scenario'"
                v-bind:key="`scenario-${item[propertyName].index}`"
                :class="`${item.scenario.noOptionLinked ? 'scenario-header-alert' : 'scenario-header '} fixed`"
              >
                <v-container class="d-flex flex-row pa-0">
                  <scenario
                    :scenario="propertyValue"
                    :show-details="debugMode"
                    :element-clickable="true"
                    @elementSelected="onElementSelected"
                  />
                </v-container>
              </th>
              <td
                v-else
                v-bind:key="`pref-${item[propertyName].index}`"
                :class="`${hasAtLeastOneScenario(propertyName) ? 'alert' : ''}`" 
              >
                <div v-if="debugMode" class="caption text-center">
                  {{ item[propertyName].preferenceId }}
                </div>
                <v-simple-checkbox
                  class="d-flex justify-center"
                  v-model="item[propertyName].value"
                  :ripple="false"
                  :disabled="true"
                  :color="item[propertyName].color"
                />
              </td>
            </template>
          </tr>
        </template>
      </v-data-table>
      </div>
    </v-container>
  </div>
</template>
<script>
import EditorStepper from '@/components/editor/EditorStepper'
import InputScenario from '@/components/editor/InputScenario'
import helpService from '@/services/help.js'
import Loader from '@/components/global/Loader'
import OptionChip2 from '@/components/global/OptionChip2'
import Scenario from '@/components/global/Scenario'

import decisionPolicyService from '@/services/decisionPolicy.js'

import debugModeMixin from '@/mixins/debug-mode-mixin.js'
import pageableMixin from '@/mixins/pageable-mixin.js'

export default ({
  name: 'DecisionPolicyViewer',

  components: {
    EditorStepper,
    InputScenario,
    Loader,
    OptionChip2,
    Scenario,
  },

  mixins: [ debugModeMixin, pageableMixin ],

  data () {
    return {
      loading: true,
      decisionPolicy: {},
      elementsFilter: [],
      optionsFilter: []
    }
  },
  
  props: {
  },

  watch: {
    options: {
      handler () {
        this.getDecisionPolicy(this.projectId, this.page, this.itemsPerPage)
      },
      deep: true,
    },
  },

  computed: {
    scenarioWithoutOptions() {
      return Object.assign({}, ...this.items.map(item => {
        return {
          [`${item.scenario.id}`]: this.reduce(item)
        }
      }))
    },
    headers() {
      return this.decisionPolicy.options.map(option => {
        return {
          value: option.id,
          option: option,
          noScenarioLinked: !decisionPolicyService.enableAtLeastOneScenario(option.id, this.decisionPolicy)
        }
      })
    },
    existsOptionWithoutScenario() {
      return this.decisionPolicy.options.some(option => this.hasAtLeastOneScenario(option.id))
    },
    existsScenarioWithoutOption() {
      return this.decisionPolicy.scenarios.some(scenario => scenario.preferences.length === 0)
    },
    items() {
      return decisionPolicyService.mapScenarios(this.decisionPolicy, this.elementsFilter, this.optionsFilter)
    },
    elementsCounter: function() {
      return this.decisionPolicy.elements.length
    },
    optionsCounter: function() {
      return this.decisionPolicy.options.length
    },
    scenariosCounter: function() {
      return this.decisionPolicy.scenarios.length
    }
  },

  methods: {
    onOptionSelected(option) {
      const index = this.optionsFilter.findIndex(opt => opt.id === option.id)
      if(index === -1) {
        this.optionsFilter.push(option)
      }
      else {
        this.optionsFilter.splice(index, 1)
      }
    },
    onElementSelected(element) {
      // console.log('onElementSelected', element)
      const index = this.elementsFilter.findIndex(elt => elt.id === element.id)
      if(index === -1) {
        this.elementsFilter.push(element)
      } // else skip
    },
    goToElementsList() {
      this.$router.push({name: 'elements', params: { projectId: this.$route.params.id }})
    },
    hasAtLeastOneScenario(optionId) {
      return this.headers.find(header => header.value === optionId).noScenarioLinked
    },
    reduce(item) {
      return Object.keys(item).reduce((result, key) => {
        if(key == 'scenario') {
          return result
        } // take care only of options properties
        return result || !item[key]
      }, false)
    },
    getDecisionPolicy(projectId) {
      this.loading = true
      // console.log('get decision policy')
      this.itemsPerPage = -1
      decisionPolicyService.getDecisionPolicyFull(projectId, this.page - 1, 10)//this.itemsPerPage)
        .then(response => {
          this.decisionPolicy = response.data
        })
        .catch(err => {
          this.$emit('raison-event', { type: 'error', messages: [err] })
        })
        .finally(() => {
          this.loading = false
        });
    },
  },

  created () {
    this.projectId = this.$route.params.id
    // this.itemsPerPage = -1
    this.getDecisionPolicy(this.projectId)
    helpService.setHelpPage(this.$store, 'editor_decision_policy')
  }
})
</script>
<style scoped>
.v-application--is-ltr .v-input--selection-controls__input {
    margin-right: 0px;
}

.v-application--is-ltr .v-input--selection-controls__input {
  margin-right: 0px;
}

.v-toolbar__title {
  text-align: center;
  text-transform: uppercase;
  font-size: 0.75rem;
  font-weight: bold;
  padding: 0.5rem;
}
.v-data-table > .v-data-table__wrapper > table {
  margin: auto;
  border-collapse: separate;
  display: block;
  overflow-x: scroll;
  height: 80px;
}

.v-data-table > .v-data-table__wrapper > table > thead {
  position: sticky;
  top: 1px;
  z-index: 2;
}

tbody {
  height: 80px;
}

tr {
  background-color: white;
}

td,
th {
  word-wrap: break-word;
}

.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  text-align: center;
}
.fixed {
  position: sticky;
  left: 0;
  top: auto;
  z-index: 2;
  padding: 0;
  border-right: 1px solid #eeeeee;
  min-width: 600px;
  max-width: 600px;
  width: 600px;
}
td:not(.fixed) {
  z-index: 1;
}
.alert, .scenario-header-alert {
  background-color: #FFECB3;
  font-weight: bold;
}
/* .scenario-header, .scenario-header-alert {
  vertical-align: bottom;
} */
.scenario-header {
  background-color: white;
  /* width: 35%;
  min-width: 300px; */
}
.v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
.v-data-table > .v-data-table__wrapper > table > tbody > tr > th,
.v-data-table > .v-data-table__wrapper > table > thead > tr > td,
.v-data-table > .v-data-table__wrapper > table > thead > tr > th,
.v-data-table > .v-data-table__wrapper > table > tfoot > tr > td,
.v-data-table > .v-data-table__wrapper > table > tfoot > tr > th {
  padding: 0;
}
tbody > tr:hover:not(.alert) {
    background-color: transparent !important;
}
tbody > .alert {
    background-color: #FFECB3 !important;
}
/* */
.background {
  width: 35%;
  min-width: 300px;
  padding: 0;
  margin: 0;
}

.line {
  width: 100%;
  height: 47px;
  border-bottom: 1px solid lightgray;
  transform: translateY(-20px) translateX(5px) rotate(27deg);
  position: absolute;
  z-index: -1;
}

.background>div {
  position: relative;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  border-bottom: thin solid rgba(0, 0, 0, 0.12);
}

.bottom {
  /* position: absolute; */
  bottom: 0px;
  left: 0px;
  float: left;
}

.top {
  position: absolute;
  top: 1px;
  right: 1px;
}

.decision-policy {
  min-width: 500px;
}
</style>